import {
  DiscoveryFilter,
  DiscoveryItem,
  BlockType,
  BlockItemType,
  SkuBlock,
  CookedDiscoveryFilter,
  BannerBlock,
  MixBannerBlock,
  MultiTabSkuBlock,
  DiscoveryBlock,
  MultiTabSkuBlockCookedItem,
} from '@teko/discovery';
import { ObjectMap } from 'types';
import { LinkProps } from 'next/link';
import { BannerEntity } from '@teko/ecommerce';
import { PromoteBlockProps } from '@teko/teko-ecommerce';
import { ServiceContainer } from './service-provider';
import { logger } from '@teko/log';
import slug from '@teko/slug';
import { getTrackingData } from '@teko/tracking';
import { cleanObject } from '@teko/utils';
import { FilterInterface, ProductCardProps } from '@teko/types/ecommerce';
import { ShopFrontGetServerSidePropsContext } from 'libs';
import {
  ITEM_PER_PAGE_IN_LISTING_PAGE,
  SSR_DESKTOP_ITEM_PER_PAGE_IN_HOME_PAGE,
} from '../constants';
import { SearchProductData, SearchProductFilter } from '@teko/discovery/v2';
import { convertToPayloadFilter, convertToPayloadSorting } from './discovery';

interface BlockProductsMap {
  [blockId: string]: ProductCardProps[];
}

const listingAndDiscoveryKeysMap = {
  priceGte: 'price_gte',
  priceLte: 'price_lte',
};
const listingAndDiscoveryValuesMap = {
  ORDER_BY_DESCENDING: 'DESC',
  ORDER_BY_ASCENDING: 'ASC',
};
export const convertObjectDiscoveryToQueryStringSearch = (
  params: ObjectMap,
): string => {
  if (!params) return '/';
  const newParams: Record<string, string | string[] | object> = cleanObject({
    ...params,
    router: 'productListing',
  });

  return (
    '/search?' +
    Object.keys(newParams)
      .map((key) => {
        if (
          !newParams[key] ||
          (Array.isArray(newParams?.[key]) &&
            (newParams[key] as string[]).length === 0)
        ) {
          return;
        }
        return (
          (listingAndDiscoveryKeysMap[
            key as keyof typeof listingAndDiscoveryKeysMap
          ] || key) +
          '=' +
          (listingAndDiscoveryValuesMap[
            newParams[key] as keyof typeof listingAndDiscoveryValuesMap
          ] || newParams[key])
        );
      })
      .filter((query) => query)
      .join('&')
  );
};

export const cookDiscoveryFilter = (
  filter: DiscoveryFilter,
): CookedDiscoveryFilter => {
  if (!filter) return {};
  if (filter?.attributes.length) {
    filter.attributes.map(
      (el) => (filter[`attributes.${el.code}`] = el.optionIds.join()),
    );
    const { attributes: tbr, ...newFilter } = filter;
    return newFilter;
  }
  return filter;
};
export const findBlockByType = (
  type: BlockType,
  blocks: DiscoveryBlock[],
): DiscoveryBlock | undefined => {
  return blocks && blocks.find((block) => block.type === type);
};
const getUrl = (item: DiscoveryItem): string => {
  const { url, redirectUrl, params } = item || {};
  return redirectUrl
    ? redirectUrl
    : url ||
        convertObjectDiscoveryToQueryStringSearch({
          ...cookDiscoveryFilter(params?.filter),
          ...(item.params?.sorting || {}),
        });
};
const getLinkHref = (
  url: string,
  paramsObject: {},
  title: string,
): LinkProps => {
  let href = null;
  if (url) {
    if (url.startsWith('/landing/')) {
      href = {
        pathname: '/landing/[id]',
        query: {
          id: url.substring('/landing/'.length),
        },
      };
    } else {
      href = url;
    }
  } else {
    // remove non-relevant flags from paramsObject
    // such as false param or empty array
    // we don't want them to appear on browser url
    /**
     *
     *  "sellingCodes": [],
        "priceLte": "0",
        "priceGte": "0",
        "brands": [],
        "flashSaleType": "",
        "masterCategories": [],
        "excludedSkus": [],
        "masterCategoryIds": [62],
      }
     */
    const queryParams: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(paramsObject)) {
      if (
        value &&
        (!Array.isArray(value) || value.length > 0) &&
        (isNaN(value as number) || !!parseInt(value as string))
      ) {
        queryParams[key] = value;
      }
    }
    href = {
      pathname: '/search',
      query: {
        ...queryParams,
        pageTitle: title,
        router: 'productListing',
      },
    };
  }
  return { href };
};

const transformBlockItemTypeSku = (block: DiscoveryBlock): SkuBlock => {
  const { fetchParams, collectionSlug = '' } = block.content;
  block.cookedData = {
    queryString: convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...fetchParams.sorting,
    }),
    paramsObject: cleanObject({
      filter: collectionSlug
        ? undefined
        : cookDiscoveryFilter(fetchParams?.filter),
      sorting: collectionSlug ? {} : fetchParams?.sorting || {},
      slug: collectionSlug,
      blockId: block.id,
    }),
  };
  return block;
};

const transformBanner = (block: DiscoveryBlock): BannerBlock => {
  const banners: BannerEntity[] = block.content.items.map((item) => {
    const paramsObject = cleanObject({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...(item.params?.sorting || {}),
      blockId: block.id,
      itemId: item.id,
    });
    return {
      id: item.id,
      targetPath: getUrl(item),
      image: item.imageUrl,
      campaignName: item.label,
      description: item.subLabel,
      paramsObject,
      url: item.url,
      thumbnail: item.thumbnail,
      link: getLinkHref(
        item.url || item.redirectUrl || '',
        paramsObject,
        item.label,
      ),
      imageSize: item.imageSize,
      as: item.url || item.redirectUrl || '',
    };
  });
  block.cookedData = banners || [];
  return block;
};

const transformBlockMixBanner = (
  block: DiscoveryBlock,
): MixBannerBlock<PromoteBlockProps> => {
  const firstItem = block?.content?.items?.[0];
  const trackingRegionName = 'categoriesBlockBrand';
  block.cookedData = {
    header: {
      title: block.label,
      link: {
        href: getUrl(firstItem),
      },
    },
    banner: {
      src: firstItem.imageUrl,
      alt: firstItem.label || firstItem.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: firstItem.label,
        target: getUrl(firstItem),
      }),
      link: {
        href: getUrl(firstItem),
      },
    },
    contentItems: block.content.items.slice(1, 5).map((category) => ({
      name: category.label,
      description: category.subLabel,
      image: {
        src: category.imageUrl,
        alt: category.label || category.subLabel,
      },
      link: {
        href: getUrl(category),
      },
    })),
    bottomImages: block.content.items.slice(5, 11).map((brand, index) => ({
      src: brand.imageUrl,
      alt: brand.label || brand.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: brand.label,
        index,
        target: getUrl(brand),
      }),
      link: {
        href: getUrl(brand),
      },
    })),
  } as PromoteBlockProps;
  return block;
};

const transformBlockHotDeal = (block: DiscoveryBlock): MultiTabSkuBlock => {
  block.cookedData = block.content.items.map((el: DiscoveryItem) => {
    const { collectionSlug, params, id } = el;
    return {
      ...el,
      name: el.label,
      url: el.redirectUrl || el.url,
      paramsObject: cleanObject({
        filter: collectionSlug
          ? undefined
          : cookDiscoveryFilter(params?.filter),
        sorting: collectionSlug ? {} : params?.sorting || {},
        slug: collectionSlug,
        blockId: block.id,
        itemId: id,
      }),
    };
  });
  return block;
};

const transformBlockListingTab = (block: DiscoveryBlock): MultiTabSkuBlock => {
  const categoryList: MultiTabSkuBlockCookedItem[] = [];
  block.content.items.forEach((item: DiscoveryItem) => {
    const { collectionSlug, params, id } = item;
    const obj = {
      ...item,
      id: item.id,
      name: item.label,
      paramsObject: cleanObject({
        filter: collectionSlug
          ? undefined
          : cookDiscoveryFilter(params?.filter),
        sorting: collectionSlug ? {} : params?.sorting || {},
        slug: collectionSlug,
        blockId: block.id,
        itemId: id,
      }),
    } as MultiTabSkuBlockCookedItem;
    categoryList.push(obj);
  });
  block.cookedData = categoryList;
  return block;
};

export const transformBlock = (block: DiscoveryBlock): DiscoveryBlock => {
  if (block.itemType === BlockItemType.SKU)
    return transformBlockItemTypeSku(block);
  else if (
    block.itemType === BlockItemType.BANNER ||
    [
      BlockType.HORIZONTAL_HIGHLIGHTED_BANNER,
      BlockType.RECOMMENDED_CATEGORY,
      BlockType.RECOMMENDED_BRANDS,
      BlockType.PROMOTE_CATEGORY_MENU,
      BlockType.HORIZONTAL_2_BANNER_MOBILE,
    ].includes(block.type)
  )
    return transformBanner(block);
  else if (block.type === BlockType.MIX_BANNER)
    return transformBlockMixBanner(block);
  else if (
    block.type === BlockType.COUNT_DOWN_PROMO_BIG_SKU ||
    block.type === BlockType.COUNT_DOWN_PROMO_TAB_MOBILE
  )
    return transformBlockHotDeal(block);
  else if (block.type === BlockType.LISTING_TAB)
    return transformBlockListingTab(block);
  else return block;
};

// fetch data for default block page (brand & seller page)
export const fetchSingleDataBlockSSRFromDiscoveryV2 = async (
  context: ShopFrontGetServerSidePropsContext,
  block: DiscoveryBlock,
  includeFilter = false,
  optionalSortingParams: { sort?: string; order?: string } = {},
): Promise<{
  filters?: SearchProductFilter;
  ssrProducts?: ProductCardProps[];
  cookFilters?: FilterInterface[] | null;
  filterPrice?: {
    max: number;
    min: number;
  };
}> => {
  const { req, query } = context;
  const { tenantEnv, location } = req;
  const { discoveryServiceV2, discoveryHelper } = new ServiceContainer(
    tenantEnv,
  );
  const { sort, order } = optionalSortingParams;

  try {
    const [{ products }, resultFilter] = await Promise.all([
      discoveryServiceV2.search({
        page: parseInt((query.page || '1').toString()),
        pageSize: ITEM_PER_PAGE_IN_LISTING_PAGE,
        slug: block?.cookedData?.paramsObject?.slug,
        filter: convertToPayloadFilter({
          ...block?.cookedData?.paramsObject?.filter,
          ...query,
        }),
        sorting: convertToPayloadSorting(sort, order),
        location,
      }),
      includeFilter
        ? await discoveryServiceV2.search({
            pageSize: 1,
            slug: block?.cookedData?.paramsObject?.slug,
            filter: convertToPayloadFilter({
              ...block?.cookedData?.paramsObject?.filter,
            }),
            returnFilterable: [
              'FILTER_TYPE_ATTRIBUTE',
              'FILTER_TYPE_BRAND',
              'FILTER_TYPE_PRICE',
              'FILTER_TYPE_CLEARANCE',
            ],
            location,
          })
        : ({} as SearchProductData),
    ]);
    return {
      ssrProducts: (products || []).map((product) =>
        discoveryHelper.cleanupDiscoveryV2SearchProduct(product),
      ),
      filters: resultFilter?.filter,
      filterPrice: {
        max: Number(resultFilter.filter?.priceLte),
        min: Number(resultFilter.filter?.priceGte),
      },
    };
  } catch (error) {
    logger.error(error);
    return {};
  }
};

export const fetchDataBlockSSRFromDiscoveryV2 = async (
  context: ShopFrontGetServerSidePropsContext,
  blocks: DiscoveryBlock[],
): Promise<BlockProductsMap> => {
  const { discoveryHelper, discoveryServiceV2 } = new ServiceContainer(
    context.req.tenantEnv,
  );
  const simpleProductBlocks = blocks.filter(
    (b) =>
      [
        BlockType.FLASH_SALE,
        BlockType.PROMOTE_SELLER,
        BlockType.BEST_SELLING_PRODUCT,
        BlockType.LIST_SKU_MOBILE,
        BlockType.MAIN_SKU_LIST,
        BlockType.COUNT_DOWN_PROMO_SKU,
        BlockType.HORIZONTAL_SKU_LIST,
      ].includes(b.type) && b.itemType === BlockItemType.SKU,
  ) as SkuBlock[];

  const partialRenderedBlocks = blocks.filter((b) =>
    [
      BlockType.COUNT_DOWN_PROMO_TAB_MOBILE,
      BlockType.COUNT_DOWN_PROMO_BIG_SKU,
      BlockType.LISTING_TAB,
    ].includes(b.type),
  ) as MultiTabSkuBlock[];

  const mapProducts = await Promise.all([
    ...simpleProductBlocks.map(async (block) => {
      const products = await discoveryServiceV2
        .search({
          slug: block?.cookedData?.paramsObject?.slug,
          filter: convertToPayloadFilter({
            ...block?.cookedData?.paramsObject?.filter,
            ...context.query,
          }),
          sorting: convertToPayloadSorting(
            context.query.sort ||
              block?.cookedData?.paramsObject?.sorting?.sort,
            context.query.order ||
              block?.cookedData?.paramsObject?.sorting?.order,
          ),
          pageSize: context.req.tenantEnv.isMobile
            ? ITEM_PER_PAGE_IN_LISTING_PAGE
            : SSR_DESKTOP_ITEM_PER_PAGE_IN_HOME_PAGE,
        })
        .then((res) =>
          (res?.products || []).map((p) =>
            discoveryHelper.cleanupDiscoveryV2SearchProduct(p, {
              hideHighlightAttribute: true,
            }),
          ),
        )
        .catch((error) => {
          logger.error('[discovery-v2]', error);
          return [] as ProductCardProps[];
        });

      return {
        [block.id]: products,
      } as BlockProductsMap;
    }),
    ...partialRenderedBlocks.map(async (block) => {
      // paramObject contant: blockId, itemId and other filter of discovery
      const firstItem = block?.cookedData?.[0];
      const blockId = block?.id;
      const itemId = firstItem?.id;
      const paramsObject = firstItem?.paramsObject;

      if (!paramsObject || !blockId || !itemId) {
        return {} as BlockProductsMap;
      }

      try {
        const result = await discoveryServiceV2.search({
          ...paramsObject,
          pageSize: 20,
          location: context.req.location,
        });
        const products = (result?.products || []).map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            hideHighlightAttribute: true,
          }),
        );
        return {
          [`${blockId}_${itemId}`]: products,
        } as BlockProductsMap;
      } catch (error) {
        logger.error(error);
        return {} as BlockProductsMap;
      }
    }),
  ]);
  return mapProducts
    .filter(Boolean)
    .reduce((acc, curr) => Object.assign(acc || {}, curr), {});
};

export const getBlockDomId = (block: DiscoveryBlock): string =>
  block.label ? slug(block.label) : `${block.name}-${block.id}`;
